'use client'

// BrowsePopup — a faux browser window that opens when Victor goes online,
// so you can watch what he's actually reading. Driven by the `browse_state`
// WS stream (sim-server runBrowseSession). Beyond the live page, it
// ACCUMULATES the session trail — every page he lands on + the thought it
// left him with — so you follow his journey, not just the current tab.
//
// Phases: start | loading | page | reacted | following | searching |
//         failed | end. The trail is built from 'reacted' (page + his
// thought); the address bar + status show the live phase.
//
// And the friend he made on this computer (sim world/Friend.js):
// friend_making | friend_made | friend_boot | friend_on | friend_look |
// friend_open | friend_line | friend_end | friend_off. friend_boot lines are
// the real start of its copy on the Pi; on, look and off are him switching
// the computer on for a talk, what it looks at first, and off again. Each
// carries its face (components/ui/FriendFace.jsx).
// While those arrive the window is a terminal, the one place on the site
// that looks like one on purpose: he is at a prompt, talking to something
// he wrote, and you watch the lines land.

import { useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { ui, kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import FriendFace from './FriendFace'

// Checked against the cream panel, not picked by eye: the old set was tuned
// for a dark browser chrome and washed out completely on this background.
const PHASE_META = {
  start:     { label: 'opening',   color: '#5f42ab' },
  loading:   { label: 'loading',   color: '#8a5a12' },
  page:      { label: 'reading',   color: '#175f83' },
  reacted:   { label: 'thinking',  color: '#276b3c' },
  following: { label: 'clicking',  color: '#5f42ab' },
  searching: { label: 'searching', color: '#9c3568' },
  failed:    { label: 'stalled',   color: '#a83832' },
  end:       { label: 'closing',   color: '#57506e' },
  friend_making: { label: 'writing somebody', color: '#9c3568' },
  friend_made:   { label: 'made somebody',    color: '#9c3568' },
  friend_boot:   { label: 'starting it',      color: '#9c3568' },
  friend_open:   { label: 'talking',          color: '#9c3568' },
  friend_line:   { label: 'talking',          color: '#9c3568' },
  friend_end:    { label: 'talking',          color: '#9c3568' },
  friend_on:     { label: 'switching it on',  color: '#9c3568' },
  friend_look:   { label: 'switched on',      color: '#9c3568' },
  friend_off:    { label: 'switched off',     color: '#9c3568' },
}

// The terminal inside the cream window. Warm near-black and cream, the
// house colours, in the one face on the site that is a computer's.
const TERM = {
  background: '#1c1715',
  color: '#f7e7d2',
  fontFamily: 'var(--font-geist-mono), ui-monospace, monospace',
}
const PROMPT = '#f0b064'
const HIM = '#a8d6a0'

function shortDomain(url) {
  if (!url) return ''
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return '' }
}

const FAIL_TEXT = {
  timeout: 'page timed out', network: "couldn't reach it", not_found: '404: gone',
  http_error: 'page errored', not_html: 'not readable, a file', invalid_url: 'broken link',
  body_read: 'fell apart loading', too_large: 'too big to take in', unreadable: 'just code and noise',
  thin: 'barely loaded', nav_page: 'mostly a list of links', llm_no_reaction: 'words slid past',
  empty_search: 'search came back empty', no_bookmarks: 'nothing to open',
  blocked_host: 'pointed somewhere it should not', too_many_redirects: 'too many redirects',
  bot_blocked: 'wanted proof he was a person',
}

// No message for this long and we assume the session died quietly.
const STALE_MS = 12000

export function BrowsePopup() {
  const browseState = useGameStore((s) => s.browseState)
  const clearBrowseState = useGameStore((s) => s.clearBrowseState)

  const [trail, setTrail] = useState([])   // [{ id, domain, title, thought, valence }]
  const [live, setLive] = useState(null)   // latest browse_state message
  const [talk, setTalk] = useState(null)   // { name, what, made, making, ended, boot: [lines], lines: [{ by, text }] }
  const seenReacted = useRef(new Set())
  const trailRef = useRef(null)
  const termRef = useRef(null)

  // Fold each incoming browse_state message into live + the trail.
  useEffect(() => {
    // Session cleared (ended, or the defensive guard below fired) — drop the
    // local mirror too. Without this the window kept rendering the last
    // message forever: clearing the store alone left `live` set, so the
    // popup sat on "victor is closing" until a reload.
    if (!browseState) {
      setLive(null)
      setTrail([])
      setTalk(null)
      seenReacted.current = new Set()
      return
    }
    setLive(browseState)

    // A new session opens on 'start' (or a search that kicks one off) —
    // wipe the previous trail so we don't blend two wanders together.
    if (browseState.phase === 'start') {
      seenReacted.current = new Set()
      setTrail([])
      setTalk(null)
    }

    // The friend: each line lands as it is said.
    const b = browseState
    if (b.phase === 'friend_making') setTalk({ name: null, what: null, making: true, made: false, ended: false, lines: [] })
    if (b.phase === 'friend_made') setTalk((t) => ({ ...(t || { lines: [] }), name: b.friend, what: b.what, making: false, made: true }))
    if (b.phase === 'friend_boot' && b.line) setTalk((t) => ({ ...(t || { name: b.friend, lines: [] }), boot: [...(t?.boot || []), b.line].slice(-6) }))
    if (b.phase === 'friend_open') setTalk((t) => (t && t.name === b.friend && !t.ended ? t : { name: b.friend, what: b.what, making: false, made: false, ended: false, boot: [], lines: [] }))
    if (b.phase === 'friend_line' && b.text) setTalk((t) => ({ ...(t || { name: b.friend, lines: [] }), lines: [...(t?.lines || []), { by: b.by, text: b.text }].slice(-12) }))
    if (b.phase === 'friend_end') setTalk((t) => (t ? { ...t, ended: true } : t))
    // switched on for the talk: what it looks at first, and off after
    if (b.phase === 'friend_on') setTalk({ name: b.friend, what: null, making: false, made: false, ended: false, boot: [], lines: [{ by: 'sys', text: 'switched on' }], face: b.face })
    if (b.phase === 'friend_look' && b.name) setTalk((t) => ({ ...(t || { name: b.friend, lines: [] }), lines: [...(t?.lines || []), { by: 'look', text: `${b.kept ? 'keeps' : 'looks at'} "${b.name}"` }].slice(-12) }))
    if (b.phase === 'friend_off') setTalk((t) => (t ? { ...t, ended: true, off: true, face: 'asleep' } : t))
    if (b.phase?.startsWith('friend_') && b.face && b.phase !== 'friend_off') setTalk((t) => (t ? { ...t, face: b.face } : t))

    // Each 'reacted' is a page he finished + the thought it left. Dedupe by
    // url+depth so a re-emit can't double it.
    if (browseState.phase === 'reacted') {
      const key = `${browseState.url}@${browseState.depth}`
      if (!seenReacted.current.has(key)) {
        seenReacted.current.add(key)
        setTrail((prev) => [
          ...prev,
          {
            id: key,
            domain: shortDomain(browseState.url),
            title: browseState.title || shortDomain(browseState.url),
            thought: browseState.thought || browseState.keyPhrase || '',
            valence: typeof browseState.valenceDelta === 'number' ? browseState.valenceDelta : null,
          },
        ].slice(-6))
      }
    }
  }, [browseState])

  // Auto-scroll the trail to the newest entry.
  useEffect(() => {
    if (trailRef.current) trailRef.current.scrollTop = trailRef.current.scrollHeight
  }, [trail.length])
  useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight
  }, [talk?.lines.length, talk?.boot?.length, talk?.ended])

  // 'end' used to close the window 2.5s later. It no longer does: he is
  // usually still at the cafe when the session ends, and whipping the trail
  // away while the HUD still says browsing is the bug above. The lifetime
  // rule below owns closing.

  // How long the window lives.
  //
  // Two clocks were disagreeing. The BROWSE action runs up to 60s, but the
  // session itself often finishes in seconds (sometimes instantly, with no
  // pages at all), and the window was following the session. So the HUD said
  // "browsing" for a minute with nothing on screen, which is what a watcher
  // notices and reports as broken.
  //
  // The window now stays for as long as he is at the cafe: while his action is
  // BROWSE, or while messages are still arriving. Only when both have stopped
  // does it close. A short session just means you read the trail for longer,
  // which is the interesting part anyway.
  const lastMsgAt = useRef(0)
  useEffect(() => { if (browseState) lastMsgAt.current = Date.now() }, [browseState])
  useEffect(() => {
    if (!browseState) return
    const id = setInterval(() => {
      const quiet = Date.now() - lastMsgAt.current > STALE_MS
      if (quiet && useGameStore.getState().npcStates?.[0]?.action !== 'BROWSE') {
        clearBrowseState()
      }
    }, 2000)
    return () => clearInterval(id)
  }, [browseState, clearBrowseState])

  if (!live) return null

  const meta = PHASE_META[live.phase] || { label: live.phase || '', color: '#9ca3af' }
  const isSearch = live.phase === 'searching'
  const who = talk?.name ? talk.name.toLowerCase().replace(/\s+/g, '-') : null
  const addressText = talk
    ? `pino@cafe ~${who ? `/${who}` : ''}`
    : isSearch
      ? `searching · "${live.query || ''}"`
      : (shortDomain(live.url) || (live.phase === 'start' ? 'opening…' : '·'))

  // The live headline under the chrome: current page title, or a status.
  const headline = isSearch
    ? `looking up "${live.query || ''}"`
    : live.phase === 'failed'
      ? (FAIL_TEXT[live.kind] || 'something stalled')
      : (live.title || shortDomain(live.url) || meta.label)

  const liveThought = live.phase === 'reacted' ? (live.thought || live.keyPhrase) : null

  return (
    <>
      <style jsx global>{`
        @keyframes bp-in { 0% { opacity: 0; transform: translateY(-50%) translateX(10px) } 100% { opacity: 1; transform: translateY(-50%) translateX(0) } }
        @keyframes bp-pulse { 0%,100% { opacity: 0.55 } 50% { opacity: 1 } }
      `}</style>
      <div
        className="fixed left-4 md:left-6 z-40 pointer-events-none"
        style={{ top: '50%', width: 'min(86vw, 330px)', animation: 'bp-in 240ms ease-out', transform: 'translateY(-50%)' }}
      >
        <div
          className="overflow-hidden"
          style={{ background: kawaii.cream, border: kawaii.outline, borderRadius: 18, boxShadow: kawaii.drop, fontFamily: ui.font }}
        >
          {/* Browser chrome: traffic-light dots + address bar + live phase */}
          <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: kawaii.stitch, background: 'rgba(47,38,80,0.05)' }}>
            <span className="flex items-center gap-1 shrink-0">
              <span className="w-2 h-2 rounded-full" style={{ background: '#ff5f57' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: '#febc2e' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: '#28c840' }} />
            </span>
            <span className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1" style={{ background: kawaii.creamDeep, border: kawaii.outlineThin, borderRadius: 999 }}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color, boxShadow: `0 0 6px ${meta.color}`, animation: 'bp-pulse 1.6s ease-in-out infinite' }} />
              <span className="text-[11px] truncate" style={{ color: kawaii.inkSoft, fontWeight: 600 }}>{addressText}</span>
            </span>
          </div>

          {/* The friend: a terminal, while he is at it. */}
          {talk && (
            <div className="px-3 pt-2.5 pb-3">
              <div className="flex items-center gap-2 mb-1.5">
                {talk.name && <FriendFace face={talk.face || 'awake'} size={30} color={kawaii.ink} marks={meta.color} title={`${talk.name}'s face`} />}
                <span className="text-[11px]" style={{ color: meta.color, fontWeight: 800 }}>
                  {talk.making && !talk.name ? `${BIRD_NAME} is writing somebody` : `${BIRD_NAME} is talking to ${talk.name}`}
                </span>
                {talk.name && <a href="/friend" className="text-[10.5px] ml-auto underline pointer-events-auto" style={{ color: kawaii.inkSoft, fontWeight: 700 }}>who is {talk.name}?</a>}
              </div>
              <div ref={termRef} className="max-h-[220px] overflow-y-auto px-2.5 py-2 text-[11.5px] leading-relaxed" style={{ ...TERM, borderRadius: 10, border: kawaii.outlineThin, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {(talk.making || talk.made) && (
                  <div><span style={{ color: PROMPT }}>$</span> {talk.name ? `made ${who}: ${talk.what || 'somebody'}` : 'writing somebody new'}{!talk.name && <span style={{ animation: 'bp-pulse 1s steps(2) infinite' }}> _</span>}</div>
                )}
                {(talk.boot || []).map((l, i) => (
                  <div key={`b${i}`} style={{ opacity: l.startsWith('$') ? 1 : 0.7 }}>{l.startsWith('$') ? <><span style={{ color: PROMPT }}>$</span>{l.slice(1)}</> : l}</div>
                ))}
                {talk.name && !(talk.boot || []).length && <div><span style={{ color: PROMPT }}>$</span> ./{who}</div>}
                {talk.lines.map((l, i) => (
                  l.by === 'sys'
                    ? <div key={i}><span style={{ color: PROMPT }}>$</span> {l.text}</div>
                    : l.by === 'look'
                      ? <div key={i} style={{ opacity: 0.65 }}>{who} {l.text}</div>
                      : <div key={i}>
                          <span style={{ color: l.by === 'pino' ? HIM : PROMPT }}>{l.by === 'pino' ? 'pino' : who}&gt;</span> {l.text}
                        </div>
                ))}
                {talk.name && !talk.ended && <span style={{ animation: 'bp-pulse 1s steps(2) infinite' }}>_</span>}
                {talk.ended && <div style={{ opacity: 0.55 }}>{talk.off ? '[screen off]' : `[${who} is quiet now]`}</div>}
              </div>
            </div>
          )}

          {/* Live viewport: what he's reading right now */}
          {!talk && <div className="px-3 pt-2.5 pb-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px]" style={{ color: meta.color, fontWeight: 800 }}>{BIRD_NAME} is {meta.label}</span>
              {live.depth > 0 && <span className="text-[10px] ml-auto" style={{ color: kawaii.inkSoft, fontWeight: 700 }}>{live.depth} deep</span>}
            </div>
            <p className="text-[13.5px] leading-snug" style={{ color: kawaii.ink, fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {headline}
            </p>
            {liveThought && (
              <p className="text-[12.5px] italic leading-snug mt-1" style={{ color: kawaii.inkSoft, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                “{liveThought}”
              </p>
            )}
          </div>}

          {/* The trail: pages he's been through this session + his thoughts */}
          {!talk && trail.length > 0 && (
            <div style={{ borderTop: kawaii.stitch }}>
              <div className="px-3 pt-2 pb-1 text-[11px]" style={{ color: kawaii.inkSoft, fontWeight: 700 }}>where he has been</div>
              <div ref={trailRef} className="kawaii-scroll max-h-[168px] overflow-y-auto px-3 pb-2.5 space-y-1.5">
                {trail.map((t) => (
                  <div key={t.id} className="leading-tight">
                    <div className="flex items-baseline gap-1.5">
                      <span className="w-1 h-1 rounded-full shrink-0 translate-y-[-1px]" style={{ background: t.valence == null ? '#57506e' : t.valence > 0.05 ? '#276b3c' : t.valence < -0.05 ? '#a83832' : '#57506e', border: `1.5px solid ${kawaii.ink}`, width: 7, height: 7 }} />
                      <span className="text-[11px] truncate" style={{ color: kawaii.inkSoft, fontWeight: 700 }}>{t.domain}</span>
                    </div>
                    <p className="text-[12px] truncate pl-2.5" style={{ color: kawaii.ink }}>{t.title}</p>
                    {t.thought && <p className="text-[11px] italic pl-2.5 truncate" style={{ color: kawaii.inkSoft }}>“{t.thought}”</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default BrowsePopup
